import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown } from "react-bootstrap";
import { LoadingOverlay, Loader } from "react-overlay-loader";

import CustomToggleSwitch from "src/components/Common/ui/Switch/CustomToggleSwitch";
import {
    IOfficeLocation,
    ISupportContact,
    ISupportContactSettingsValues,
} from "src/reducers/adminSettingsReducer";
import {
    MessageOptionsConstants,
    SupportContactsConstants,
    userSettingsConstant,
} from "src/Core/Common/Constants";
import SearchInput from "src/components/Common/SearchComponent/SearchComponent";
import { ApplicationState } from "src/store";
import {
    fetchContactList,
    fetchOfficeLocationList,
    fetchSupportContactSettings,
    updateAdminSettings,
} from "src/actionCreators/adminSettingsActionCreators";
import { CompanySettingsName } from "src/Core/Common/Enums";
import { ResourceIdLocators } from "src/helper/ResourceIdLocators";
import { CustomModal } from "src/components/Common/Modal/CustomModal";
import { sortContactsByFullName, sortLocationsByName } from "src/helper/HelperFunctions";


const ContactSupport: React.FC = () => {
    const [selectedContact, setSelectedContact] = useState<ISupportContact>({
        firstName: "",
        lastName: "",
        emailAddress: "",
        userId: 0,
    }),
        noUserOption = { firstName: "No User", lastName: "", userId: 0, emailAddress: "" },
        [selectedOfficeLocation, setSelectedOfficeLocation] = useState<IOfficeLocation>({
            name: "",
            locationId: 0,
            isPrimary: false,
        }),
        [filteredOfficeLocationList, setFilteredOfficeLocationList] = useState<IOfficeLocation[]>([]),
        [officeLocationList, setOfficeLocationList] = useState<IOfficeLocation[]>([]),
        [contactList, setContactList] = useState<ISupportContact[]>([]),
        [showAuthMethodWarningModal, setAuthMethodWarningModal] = useState(false),
        [allowEmployee, setAllowEmployee] = useState<boolean>(false),
        [showPhone, setShowPhone] = useState<boolean>(true),
        [searchText, setSearchText] = useState<string>(""),
        [filteredList, setFilteredList] = useState<ISupportContact[]>([]),
        [searchTextOfficeLocation, setSearchTextOfficeLocation] = useState<string>("");

    const dispatch = useDispatch();

    const { isLoading } = useSelector((state: ApplicationState) => state.adminSettingsState);
    const [supportContactList, setSupportContactList] = useState<ISupportContact[]>([]);
    const [supportContactSettings, setSupportContactSettings] = useState<ISupportContactSettingsValues | null>(null);


    useEffect(() => {
        dispatch(
            fetchOfficeLocationList((locations: IOfficeLocation[]) => {
                setOfficeLocationList(locations);
                const sortedLocations = sortLocationsByName(locations);
                setFilteredOfficeLocationList(sortedLocations);

                dispatch(
                    fetchSupportContactSettings((settings: ISupportContactSettingsValues) => {
                        setSupportContactSettings(settings);
                        const selectedLocation = locations.find(data => data.locationId === settings.locationId);
                        const locationId = selectedLocation ? selectedLocation.locationId : 1;

                        dispatch(
                            fetchContactList(locationId, (contacts: ISupportContact[]) => {
                                setSupportContactList(contacts);
                                const sortedContacts = sortContactsByFullName(contacts);
                                setFilteredList([noUserOption, ...sortedContacts]);
                            })
                        );
                    })
                );

            }));
    }, []);


    useEffect(() => {
        if (supportContactSettings && supportContactList.length > 0) {
            setContactList([noUserOption, ...supportContactList]);
            let defaultContact: any = noUserOption;
            if (supportContactSettings.supportContactId !== 0) {
                defaultContact = supportContactList.find(
                    data => data.userId === supportContactSettings.supportContactId
                );
            }

            const displayPhone = !!supportContactSettings?.isDisplayPhoneEnabled,
                allowEmployeesToChangeSupportContact = !!supportContactSettings?.allowEmployeesToChangeSupportContact;
            setShowPhone(displayPhone);
            setAllowEmployee(allowEmployeesToChangeSupportContact);
            const selectedLocation = filteredOfficeLocationList.find(data => data.locationId === supportContactSettings.locationId);
            if (!selectedLocation) { // if selected location is null, then set primary contact as default
                const primaryAdmin = getPrimaryAdminForCompany();
                primaryAdmin && setSelectedContact(primaryAdmin);
            }
            else {
                if (defaultContact) {
                    setSelectedContact(defaultContact);
                }
                else {
                    const primaryAdmin = getPrimaryAdminForCompany();
                    primaryAdmin && setSelectedContact(primaryAdmin);
                }

            }
        }
    }, [supportContactSettings, supportContactList]);

    useEffect(() => {
        if (supportContactSettings && officeLocationList.length > 0) {
            const selectedLocation = filteredOfficeLocationList.find(
                data => data.locationId === supportContactSettings.locationId
            );

            if (selectedLocation) {
                setSelectedOfficeLocation(selectedLocation);
            }
            else {
                setPrimaryLocation();

            }
        }
    }, [supportContactSettings, officeLocationList]);


    const getName = (contact: ISupportContact): string => `${contact?.firstName} ${contact?.lastName || ""}`,
        getContactPayload = (
            contactId: number,
            displayPhone: boolean,
            locationId: number,
            allowEmployeesToChangeSupportContact: boolean
        ) => {
            return {
                supportContactId: contactId,
                isDisplayPhoneEnabled: displayPhone,
                locationId: locationId,
                allowEmployeesToChangeSupportContact: allowEmployeesToChangeSupportContact,
            };
        },
        onContactChange = (e: any) => {
            let contact: any = null;
            if (e.target.id == "contact-0") {
                contact = noUserOption;
            } else {
                contact = supportContactList.find((data: ISupportContact) => `contact-${data.userId}` === e.target.id);
            }
            const location = selectedOfficeLocation || filteredOfficeLocationList[0];

            if (contact) {
                setSelectedContact(contact);
                const contactPayload = getContactPayload(
                    contact?.userId,
                    showPhone,
                    location?.locationId,
                    allowEmployee
                );
                dispatch(updateAdminSettings(CompanySettingsName.SupportContactSettings, contactPayload));

                const tempSupportContactSettings = {
                    supportContactId: contact?.userId,
                    isDisplayPhoneEnabled: supportContactSettings?.isDisplayPhoneEnabled,
                    locationId: supportContactSettings?.locationId,
                    allowEmployeesToChangeSupportContact: supportContactSettings?.allowEmployeesToChangeSupportContact,
                } as ISupportContactSettingsValues;
                setSupportContactSettings(tempSupportContactSettings);
            }
        },
        onOfficeLocationChange = (e: any) => {
            const location = filteredOfficeLocationList.find(
                (data: IOfficeLocation) => `contact-${data.locationId}` === e.target.id
            );

            const contact = selectedContact || supportContactList[0];

            if (location) {
                setSelectedOfficeLocation(location);
                dispatch(fetchContactList(location.locationId, (contacts: ISupportContact[]) => {
                    setSupportContactList(contacts);
                    const sortedContacts = sortContactsByFullName(contacts);
                    setFilteredList([noUserOption, ...sortedContacts]);
                    const primaryAdmin = [...sortedContacts].find(contact => {
                        if (contact?.isPrimary) {
                            return contact;
                        }
                    });

                    setSelectedContact(primaryAdmin || noUserOption);

                    const payload = getContactPayload(primaryAdmin?.userId ?? noUserOption.userId, showPhone, location?.locationId, allowEmployee);
                    dispatch(updateAdminSettings(CompanySettingsName.SupportContactSettings, payload));

                    const tempSupportContactSettings = {
                        supportContactId: supportContactSettings?.supportContactId, isDisplayPhoneEnabled: supportContactSettings?.isDisplayPhoneEnabled, locationId: location.locationId,
                        allowEmployeesToChangeSupportContact: supportContactSettings?.allowEmployeesToChangeSupportContact
                    } as ISupportContactSettingsValues;
                    setSupportContactSettings(tempSupportContactSettings);
                }));
            }
        },
        onShowPhoneChange = (e: any) => {
            setShowPhone(e.target.checked);
            const contact = selectedContact || supportContactList[0];
            const location = selectedOfficeLocation || filteredOfficeLocationList[0];
            const contactPayload = getContactPayload(contact?.userId, e.target.checked, location?.locationId, allowEmployee);
            dispatch(updateAdminSettings(CompanySettingsName.SupportContactSettings, contactPayload));
            const tempSupportContactSettings = {
                supportContactId: supportContactSettings?.supportContactId,
                isDisplayPhoneEnabled: e.target.checked,
                locationId: supportContactSettings?.locationId,
                allowEmployeesToChangeSupportContact: supportContactSettings?.allowEmployeesToChangeSupportContact
            } as ISupportContactSettingsValues;
            setSupportContactSettings(tempSupportContactSettings);
        },
        onAllowEmployeeChange = (e: any) => {
            const value = e.target.checked;
            if (!value) {
                setAuthMethodWarningModal(true);
            }
            else {
                setAllowEmployee(e.target.checked);
                const contact = selectedContact || supportContactList[0];
                const location = selectedOfficeLocation || filteredOfficeLocationList[0];
                const contactPayload = getContactPayload(contact.userId, showPhone, location?.locationId, e.target.checked);
                dispatch(updateAdminSettings(CompanySettingsName.SupportContactSettings, contactPayload));
                const tempSupportContactSettings = {
                    supportContactId: supportContactSettings?.supportContactId,
                    isDisplayPhoneEnabled: supportContactSettings?.isDisplayPhoneEnabled,
                    locationId: supportContactSettings?.locationId,
                    allowEmployeesToChangeSupportContact: value,
                } as ISupportContactSettingsValues;
                setSupportContactSettings(tempSupportContactSettings);
            }
        },
        handleAuthMethodWarningModalSubmission = () => {
            setAuthMethodWarningModal(false);
            setAllowEmployee(false);
            const contact = selectedContact || supportContactList[0];
            const contactPayload = getContactPayload(contact.userId, showPhone, selectedOfficeLocation?.locationId, false);
            dispatch(updateAdminSettings(CompanySettingsName.SupportContactSettings, contactPayload));
            const tempSupportContactSettings = {
                supportContactId: supportContactSettings?.supportContactId,
                isDisplayPhoneEnabled: supportContactSettings?.isDisplayPhoneEnabled,
                locationId: supportContactSettings?.locationId,
                allowEmployeesToChangeSupportContact: false
            } as ISupportContactSettingsValues;
            setSupportContactSettings(tempSupportContactSettings);
        },
        onFilter = (e: any) => {
            const value = e.target.value;
            setSearchText(value);
            const filteredList = contactList.filter(data => {
                if (value.toLocaleLowerCase().trim() != noUserOption.firstName.toLocaleLowerCase().trim()) {
                    return `${getName(data).toLocaleLowerCase()}`.includes(value.toLocaleLowerCase());
                }
                else {
                    return false;
                }
            });
            setFilteredList(filteredList);
        },
        onFilterOfficeLocation = (e: any) => {
            const value = e.target.value;
            setSearchTextOfficeLocation(value);
            let filteredList = officeLocationList.filter(data =>
                `${data?.name.toLocaleLowerCase()}`.includes(value.toLocaleLowerCase())
            );

            filteredList = sortLocationsByName(filteredList);
            setFilteredOfficeLocationList(filteredList);
        },
        getPrimaryAdminForCompany = () => {
            return supportContactList.find(data => data.isPrimary === true);
        },
        setPrimaryLocation = () => {
            const primaryLocation = filteredOfficeLocationList.find(l => l.isPrimary === true);

            if (primaryLocation) {
                setSelectedOfficeLocation(primaryLocation);
            }
        };

    return (
        <LoadingOverlay>
            <header>
                <div><span>{SupportContactsConstants.SUPPORT_CONTACT_HELP_TEXT_LINE1}</span></div>
                <p><span>{SupportContactsConstants.SUPPORT_CONTACT_HELP_TEXT_LINE2}</span></p>
            </header>

            <section className="margin-top-10-px">
                <div className="support-contact-wrapper contact-label-dropdown-wrapper">
                    <h4 className="heading-black-md">{SupportContactsConstants.SUPPORT_CONTACT_DROP_DOWN_OFFICE_LOCATION_TEXT}</h4>

                    <Dropdown className="btn-support-contact" style={{ marginLeft: "129px" }}>
                        <Dropdown.Toggle className="dropdown-toggle">
                            <p
                                title={selectedOfficeLocation ? selectedOfficeLocation.name : filteredOfficeLocationList[0]?.name}
                                className="overflowText"
                            >
                                {selectedOfficeLocation ? selectedOfficeLocation.name : filteredOfficeLocationList[0]?.name}
                            </p>
                        </Dropdown.Toggle>

                        <Dropdown.Menu className="support-contact-dropdown-menu">
                            <div className="dropdown-search">
                                <SearchInput placeholder="Search" value={searchTextOfficeLocation} onChange={onFilterOfficeLocation} />
                            </div>
                            <div>
                                {filteredOfficeLocationList.length > 0 ? (
                                    filteredOfficeLocationList.map(data => (
                                        <Dropdown.Item
                                            title={data?.name}
                                            id={`contact-${data.locationId}`}
                                            eventKey={data.locationId}
                                            key={data.locationId}
                                            className={
                                                (selectedOfficeLocation?.locationId === data.locationId ? "selected-contact" : "") +
                                                " overflowText"
                                            }
                                            onClick={e => onOfficeLocationChange(e)}
                                        >
                                            {data.name}
                                        </Dropdown.Item>
                                    ))
                                ) : (
                                    <Dropdown.Item>{userSettingsConstant.NO_LOCATION_FOUND_TEXT}</Dropdown.Item>
                                )}
                            </div>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </section>

            <section className="margin-top-5-px">
                <div className="support-contact-wrapper contact-label-dropdown-wrapper">
                    <h5>{SupportContactsConstants.SUPPORT_CONTACT_DROP_DOWN_TEXT}</h5>

                    <Dropdown className="btn-support-contact" style={{ marginLeft: "121px" }}>
                        <Dropdown.Toggle className="dropdown-toggle">
                            <p
                                title={selectedContact ? getName(selectedContact) : getName(supportContactList[0])}
                                className="truncate"
                            >
                                {selectedContact ? getName(selectedContact) : getName(supportContactList[0])}
                            </p>
                        </Dropdown.Toggle>

                        <Dropdown.Menu className="support-contact-dropdown-menu">
                            <div className={"dropdown-search"}>
                                <SearchInput
                                    placeholder="Search"
                                    value={searchText}
                                    onChange={onFilter}
                                    isCollapsable={false}
                                />
                            </div>
                            <div>
                                {filteredList.length > 0 ? (
                                    filteredList.map(data => (
                                        <Dropdown.Item
                                            title={getName(data)}
                                            id={`contact-${data.userId}`}
                                            eventKey={data.userId}
                                            key={data.userId}
                                            className={
                                                (selectedContact?.userId === data.userId ? "selected-contact" : "") +
                                                " truncate"
                                            }
                                            onClick={e => onContactChange(e)}
                                            data-email={data.emailAddress}
                                        >
                                            {getName(data)}
                                        </Dropdown.Item>
                                    ))
                                ) : (
                                    <Dropdown.Item>{userSettingsConstant.NO_DELEGATEE_TEXT}</Dropdown.Item>
                                )}
                            </div>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </section>

            <div className="settings-label-dropdown-wrapper contact-label-dropdown-wrapper">
                <CustomToggleSwitch
                    label={SupportContactsConstants.SUPPORT_CONTACT_TOOGLE_SW_TEXT}
                    handleChange={onShowPhoneChange}
                    switched={showPhone}
                />
            </div>

            <div className="contact-label-dropdown-wrapper">
                <CustomToggleSwitch
                    label={SupportContactsConstants.SUPPORT_CONTACT_TOOGLE_CHANGE_SUPPORT_CONTACT_TEXT}
                    handleChange={onAllowEmployeeChange}
                    switched={allowEmployee}
                />
            </div>
            <hr />

            {showAuthMethodWarningModal && (
                <CustomModal
                    show={showAuthMethodWarningModal}
                    cancelButtonName="No"
                    confirmButtonName="Yes"
                    title={"Confirm Selection"}
                    isConfirmModal={true}
                    className="message-options-auth-method-warning-modal"
                    onSubmit={handleAuthMethodWarningModalSubmission}
                    onHide={() => setAuthMethodWarningModal(false)}
                >
                    {MessageOptionsConstants.CHANGE_CONTACT_SUPPORT_METHOD_WARNING_TEXT}
                </CustomModal>
            )}


            <hr />
            <Loader loading={isLoading} />
        </LoadingOverlay>
    );
};

export default ContactSupport;
